'use client';

import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils';
import { Package, Calculator } from 'lucide-react';
import type { POItemFormState } from '@/types/purchase-order-form';

interface POTotalsSummaryProps {
  items: POItemFormState[];
  className?: string;
}

export function POTotalsSummary({ items, className }: POTotalsSummaryProps) {
  const subtotal = items.reduce((sum, item) => sum + item.line_total, 0);
  const itemCount = items.length;

  // Tax placeholder (would come from settings)
  const taxRate = 0.11; // 11% VAT in Indonesia
  const tax = subtotal * taxRate;
  const total = subtotal + tax;

  return (
    <div className={cn("border rounded-lg p-4 bg-gray-50 space-y-4", className)}>
      <div className="flex items-center gap-2 pb-3 border-b">
        <Calculator className="h-5 w-5 text-primary" />
        <h3 className="font-semibold">Order Summary</h3>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-gray-400" />
            <span>Items ({itemCount})</span>
          </div>
          <span className="font-mono">{formatCurrency(subtotal)}</span>
        </div>

        <div className="flex items-center justify-between text-sm">
          <span>Tax (11% VAT)</span>
          <span className="font-mono">{formatCurrency(tax)}</span>
        </div>

        <div className="border-t pt-3">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-base">Total</span>
            <span className="font-mono font-bold text-xl text-primary">
              {formatCurrency(total)}
            </span>
          </div>
        </div>
      </div>

      {items.length === 0 && (
        <p className="text-xs text-gray-500 text-center py-2">
          Add items to see total calculation
        </p>
      )}
    </div>
  );
}
